/* Native Shopify customer form with a background transport. Install the form's
 * submit transport BEFORE Shopify binds its CAPTCHA wrapper; protect() then
 * runs Shopify's verification before calling that transport. Never call hCaptcha
 * directly or infer success
 * from an HTTP 200. No customer information is kept in browser storage here.
 */
(() => {
  if (window.SahrawiNewsletter) return;
  const init = (scope = document) => {
    scope.querySelectorAll('[data-newsletter] form').forEach((form) => {
      const input = form.querySelector('[name="contact[email]"]');
      if (!input || form.dataset.newsletterReady) return;
      const button = form.querySelector('button[type="submit"]');
      const label = form.querySelector('[data-newsletter-button]');
      const status = form.querySelector('[data-newsletter-status]');
      const error = form.querySelector('[data-newsletter-error]');
      if (!button || !label || !status || !error) return;
      // If a third party has already wrapped submission, preserve its behaviour
      // rather than accidentally bypassing protection. Normal page initialization
      // runs before interaction; section reloads receive fresh forms.
      if (form.submit !== HTMLFormElement.prototype.submit) return;
      form.dataset.newsletterReady = 'true';
      const originalLabel = label.textContent;
      const originalDescription = input.getAttribute('aria-describedby') || '';
      let pending = false, completed = false, sending = false, timeout;
      const finish = () => {
        pending = false;
        clearTimeout(timeout);
        form.removeAttribute('aria-busy');
        button.disabled = completed;
        label.textContent = completed ? 'Subscribed' : originalLabel;
      };
      const fail = (message, challengeUrl) => {
        error.textContent = message;
        if (challengeUrl) {
          const link = document.createElement('a');
          link.href = challengeUrl;
          link.textContent = 'Complete the security check';
          error.append(' ', link);
        }
        error.hidden = false;
        status.hidden = true;
        input.setAttribute('aria-describedby', `${originalDescription} ${error.id}`.trim());
        finish();
      };
      const send = async () => {
        if (!pending || sending || completed) return;
        sending = true;
        try {
          const result = await fetch(form.action, {
            method: 'POST', body: new FormData(form), credentials: 'same-origin',
            signal: AbortSignal.timeout(30000),
          });
          const url = new URL(result.url);
          if (url.origin !== location.origin) throw new Error('Unexpected response');
          if (/\/challenge\/?$/.test(url.pathname)) {
            fail('Shopify needs a security check before confirming your signup.', url.href);
            return;
          }
          const response = new DOMParser().parseFromString(await result.text(), 'text/html');
          const returnedForm = response.getElementById(form.id);
          const success = returnedForm?.querySelector('.sf-newsletter__status:not([hidden])');
          const serverError = returnedForm?.querySelector('.sf-newsletter__error:not([hidden])');
          if (result.ok && success?.textContent.trim()) {
            form.style.minHeight = `${form.getBoundingClientRect().height}px`;
            form.dataset.newsletterComplete = 'true';
            completed = true;
            input.readOnly = true;
            status.textContent = success.textContent.trim();
            status.hidden = false;
            error.hidden = true;
            finish();
          } else if (serverError?.textContent.trim()) {
            input.setAttribute('aria-invalid', 'true');
            fail(serverError.textContent.trim());
          } else fail('We could not confirm your signup. Please try again.');
        } catch {
          fail('We could not confirm your signup. Please check your connection and try again.');
        } finally {
          sending = false;
        }
      };
      form.submit = send;
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (pending || completed) {
          return;
        }
        if (!form.checkValidity()) return;
        pending = true;
        error.hidden = true;
        status.hidden = true;
        input.removeAttribute('aria-invalid');
        input.setAttribute('aria-describedby', originalDescription);
        button.disabled = true;
        label.textContent = 'Joining…';
        form.setAttribute('aria-busy', 'true');
        timeout = setTimeout(() => fail('Your signup has not been confirmed yet. Please check your connection before trying again.'), 60000);
        if (window.Shopify?.captcha?.protect) {
          // Supported Shopify API. Its wrapper adds its token and form_key
          // before invoking our transport; no direct CAPTCHA library calls.
          window.Shopify.captcha.protect(form, () => {
            if (!pending) return;
            if (form.submit === send) {
              fail('The security check could not load. Please reload and try again.');
              return;
            }
            form.submit();
          });
        } else send();
      }, true);
      input.addEventListener('input', () => {
        input.removeAttribute('aria-invalid');
        if (!pending) error.hidden = true;
      });
    });
  };
  window.SahrawiNewsletter = { init };
  init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
